<template>
  <div v-if="quote">
    <div class="configuration-action-buttons__wrapper">
      <div v-if="showSaveButton" class="configuration-action-buttons__concept-actions">
        <span>
          <save-quote-button :disabled="isSaveQuoteButtonDisabled" />
        </span>
        <download-pdf-button
          v-if="showDownloadConceptPdfButton"
          :disabled="isSaveQuoteButtonDisabled"
          :quote-id="quote.QuoteId"
          title="Concept PDF"
          type="CONCEPT_PDF"
        />
      </div>

      <div class="configuration-action-buttons__invoice-actions">
        <send-offer
          :disabled="isSendOfferButtonDisabled"
          :title="i18n.t('/QuoteActions/SendOfferButton')"
          :check-customer-type="checkCustomerType"
          :check-expire-date="checkExpireDate"
          :check-expected-delivery-date="checkExpectedDeliveryDate"
        />
        <download-pdf-button
          :disabled="isOfferPdfButtonDisabled"
          :quote-id="quote.QuoteId"
          :title="i18n.t('/QuoteActions/Download Offer Button')"
          :check-customer-type="checkCustomerType"
          type="OFFER_PDF"
        />
      </div>

      <div class="configuration-action-buttons__order-actions">
        <send-order
          :disabled="isSendOrderButtonDisabled"
          :check-customer-type="checkCustomerType"
          :check-expire-date="checkExpireDate"
          :check-expected-delivery-date="checkExpectedDeliveryDate"
        />
        <download-pdf-button
          :disabled="isOrderPdfButtonDisabled"
          :quote-id="quote.QuoteId"
          :title="i18n.t('/QuoteActions/Download Order Button')"
          type="ORDER_PDF"
        />
      </div>
    </div>

    <!-- Approval Actions (only shown in approval mode) -->
    <div v-if="canShowApprovalButtons" class="configuration-action-buttons__approval-actions">
      <app-button
        button-type="primary"
        data-qa="approve-order-button"
        :disabled="isProcessingApproval"
        @click="openApprovalModal('approve')"
      >
        {{ i18n.t('/ManagerApproval/Actions/Approve') || 'Goedkeuren' }}
      </app-button>

      <app-button
        button-type="danger"
        data-qa="reject-order-button"
        :disabled="isProcessingApproval"
        @click="openApprovalModal('reject')"
      >
        {{ i18n.t('/ManagerApproval/Actions/Reject') || 'Afkeuren' }}
      </app-button>
    </div>

    <div class="configuration-action-warning__wrapper">
      <alert v-if="showNoCustomerAlert" type="info" text="Voeg klant toe om order te versturen." />
    </div>

    <!-- Approval Modal -->
    <approval-modal
      v-if="showApprovalModal"
      :type="approvalType"
      :delivery-date-changed="deliveryDateChanged"
      :new-delivery-date="newDeliveryDate"
      :is-processing="isProcessingApproval"
      @close="closeApprovalModal"
      @confirm="handleApprovalConfirm"
    />
  </div>
</template>

<script>
import SendOffer from '@/components/generic/presentational/organisms/quoteActions/SendOffer.vue'
import SendOrder from '@/components/generic/presentational/organisms/quoteActions/SendOrder.vue'
import DownloadPdfButton from '@/components/generic/presentational/molecules/downloadButton/DownloadPdfButton.vue'
import SaveQuoteButton from '@/components/generic/presentational/molecules/saveQuoteButton/SaveQuoteButton.vue'
import Alert from '@/components/generic/presentational/atoms/alert/Alert.vue'
import AppButton from '@/components/generic/presentational/atoms/appButton/AppButton.vue'
import ApprovalModal from '@/components/generic/presentational/molecules/approvalModal/ApprovalModal.vue'
import { mapState, mapActions } from 'vuex'
import AdditionalOrder from '@/mixins/additionalOrder.js'
import managerApprovalService from '@/services/managerApprovalService'

export default {
  components: {
    DownloadPdfButton,
    SendOrder,
    SendOffer,
    SaveQuoteButton,
    AppButton,
    ApprovalModal,
    Alert,
  },
  mixins: [AdditionalOrder],
  props: {
    showSaveButton: {
      type: Boolean,
      default: false,
    },
    checkCustomerType: {
      type: Boolean,
      default: false,
    },
    checkExpireDate: {
      type: Boolean,
      default: false,
    },
    checkExpectedDeliveryDate: {
      type: Boolean,
      default: false,
    },
  },
  data() {
    return {
      i18n: window.VUE_CLI_EXPORTS.plugins.i18n,
      showApprovalModal: false,
      approvalType: null,
      isProcessingApproval: false,
      showSendOrderModal: false,
    }
  },
  computed: {
    // Dynamic namespaces aren't supported by mapGetters
    ...mapState({
      approvalModeEnabled(state, getters) {
        return getters['user/approvalModeEnabled']
      },
      quote(state, getters) {
        return getters[this.quoteNamespace + '/get']
      },
      isLoading(state, getters) {
        return getters[this.quoteNamespace + '/loading']
      },
      quoteCustomer(state, getters) {
        return getters[this.quoteNamespace + '/customer']
      },
      isStatusSalesOrder(state, getters) {
        return getters[this.quoteNamespace + '/quoteStatusIsSalesOrder']
      },
      isStatusQuote(state, getters) {
        return getters[this.quoteNamespace + '/quoteStatusIsQuote']
      },
      isStatusConcept(state, getters) {
        return getters[this.quoteNamespace + '/quoteStatusIsConcept']
      },
      isStockOrder(state, getters) {
        return getters[this.quoteNamespace + '/isStockOrder']
      },
      hasOwner(state, getters) {
        return getters[this.quoteNamespace + '/hasOwner']
      },
      hasCustomer(state, getters) {
        return getters[this.quoteNamespace + '/hasCustomer']
      },
      isConfigurationValid(state, getters) {
        return getters['configuration/isConfigurationValid']
      },
      isStockCarAvailable(state, getters) {
        return getters['configuration/isStockCarAvailable']
      },
      isQuoteNew(state, getters) {
        return getters[this.quoteNamespace + '/isNew']
      },
      customerType(state, getters) {
        return getters[this.quoteNamespace + '/customerType']
      },
    }),
    canShowApprovalButtons() {
      return (
        this.approvalModeEnabled &&
        (this.quote.Status === 'TO_APPROVE' || this.quote.Status === 'APPROVED')
      )
    },
    isConfigurationInvalid() {
      return this.isConfigurationValid === false
    },
    isSaveQuoteButtonDisabled() {
      return false
    },
    showDownloadConceptPdfButton() {
      return !this.additionalQuote && this.isConfigurationInvalid
    },
    isSendOfferButtonDisabled() {
      if (this.quoteIsInvalid || this.isLoading) {
        return true
      }

      if (this.customerCmdmHasFailed || this.isStockOrder) {
        return true
      }

      if ((!this.additionalQuote && this.isQuoteNew) || this.isStatusSalesOrder) {
        // if quote is not saved, or it is a salesorder
        return true
      }

      return false
    },
    isOfferPdfButtonDisabled() {
      if (this.quoteIsInvalid || this.isLoading) {
        return true
      }

      if (this.additionalQuote) {
        return false
      }

      if (this.isQuoteNew || this.isStatusConcept) {
        return true
      }

      return false
    },
    isSendOrderButtonDisabled() {
      if (this.isStockOrder) {
        return false
      }

      if (this.quoteIsInvalid || !this.hasCustomer || this.isLoading) {
        // quote should be valid, have a customer and the correct status
        return true
      }
      if (this.isStatusSalesOrder) {
        return true
      }

      if (
        !this.additionalQuote &&
        (!this.isStatusQuote || this.quoteSendToAutolineHasFailed || this.customerCmdmHasFailed)
      ) {
        return true
      }

      return false
    },

    isOrderPdfButtonDisabled() {
      if (this.quoteIsInvalid || !this.isStatusSalesOrder || this.isLoading) {
        return true
      }

      return false
    },
    quoteSendToAutolineHasFailed() {
      // status is quote, but send to autoline failed.
      return this.isStatusQuote && !this.quote.QuoteSendToAutoline
    },
    customerCmdmHasFailed() {
      return this.hasCustomer && !!this.quoteCustomer.CustomerHasCmdmFailed
    },
    quoteIsInvalid() {
      // if configuration invalid, or stockcar is unavailable, or there is no owner
      return (
        (this.isConfigurationInvalid && !this.additionalQuote) ||
        !this.hasOwner ||
        !this.isStockCarAvailable
      )
    },
    showNoCustomerAlert() {
      return !this.hasCustomer && this.isStatusQuote
    },
    deliveryDateChanged() {
      // TODO CST-3970: Implement logic to detect if delivery date was changed during approval
      // This would require comparing original delivery date with current one
      return false
    },
    newDeliveryDate() {
      // TODO CST-3970: Return formatted new delivery date if changed
      return null
    },
    quoteId() {
      return this.quote?.QuoteId
    },
    quoteStatus() {
      return this.quote?.Status
    },
    currentUser() {
      return window.info?.user?.UserInfo?.UserName
    },
  },
  created() {},
  methods: {
    toggleSendOrderModal(show) {
      this.showSendOrderModal = show
    },
    openApprovalModal(type) {
      this.approvalType = type
      this.showApprovalModal = true
    },
    closeApprovalModal() {
      this.showApprovalModal = false
      this.approvalType = null
    },
    async handleApprovalConfirm(reason) {
      if (!this.quoteId || !this.currentUser) {
        console.error('Missing required data for approval')
        return
      }

      this.isProcessingApproval = true

      try {
        if (this.approvalType === 'approve') {
          await managerApprovalService.approveOrder(this.quoteId, this.quoteStatus, reason)
        } else if (this.approvalType === 'reject') {
          await managerApprovalService.rejectOrder(this.quoteId, this.quoteStatus, reason)
        }

        // Close modal and navigate back
        this.closeApprovalModal()

        // Navigate back to the approval dashboard
        const returnUrl = this.$route.query.returnUrl
        if (returnUrl) {
          this.$router.push(returnUrl)
        } else {
          this.$router.push({
            name: 'quote-overview-page',
            params: {
              cstType: this.$route.params.cstType,
              establishmentId: this.$route.params.establishmentId,
            },
          })
        }
      } catch (error) {
        console.error('Error processing approval:', error)
        // TODO: Show error message to user
        alert('Er is een fout opgetreden bij het verwerken van de beoordeling.')
      } finally {
        this.isProcessingApproval = false
      }
    },
  },
}
</script>

<style lang="scss" scoped>
.quoteActionsButton {
  margin-bottom: 0;
}

.configuration-action-buttons__wrapper {
  display: flex;
  justify-content: space-between;
}

.configuration-action-buttons__order-actions label {
  margin-right: 2em;
  font-weight: bold;
}

.configuration-action-buttons__quote-actions {
  display: inline-block;
}

.configuration-action-warning__wrapper {
  display: flex;
  justify-content: flex-end;
}

.configuration-action-buttons__approval-actions {
  display: flex;
  justify-content: center;
  gap: 1rem;
  margin: 2rem 0;
  padding: 1.5rem;
  background-color: #f8f9fa;
  border: 2px solid #e9ecef;
  border-radius: 8px;
}
</style>
